// src/types/ringcentral.ts
// RingCentral OAuth Integration Service - TypeScript Type Definitions
// Comprehensive type definitions for RingCentral OAuth flow and data models
// RELEVANT FILES: src/api/ringcentral-api.ts, src/stores/ringcentral.ts, src/composables/useRingCentral.ts

import type { BusinessConfig } from './organization-config';

/**
 * Request payload for creating a new organization
 */
export interface CreateOrganizationRequest {
  name: string;
  franchise: 'orangeTheory';
  description?: string;
  domain?: string;
  industry?: string;
  size?: '1-10' | '11-50' | '51-200' | '201-500' | '501-1000' | '1001-5000' | '5000+';
  location?: string;
  timezone?: string;
  contactEmail?: string;
  contactPhone?: string;
  website?: string;
  portalOrgId?: string;
  parentOrgId?: string;
  businessConfig?: BusinessConfig;
  leadEmails?: string[];
}

/**
 * Organization entity from RingCentral service
 */
export interface Organization {
  organizationId: string;
  name: string;
  franchise: 'orangeTheory';
  description?: string;
  timezone: string;
  createdAt: string;
  updatedAt?: string;
  status?: 'active' | 'suspended' | 'pending';
  domain?: string;
  industry?: string;
  size?: string;
  location?: string;
  contactEmail?: string;
  contactPhone?: string;
  website?: string;
  businessConfig?: BusinessConfig;
  leadEmails?: string[];
  metadata?: {
    siteId?: string;
    [key: string]: unknown;
  };
  // OAuth connection status
  hasOAuth?: boolean;
  oauthConnection?: {
    hasOAuth?: boolean;
    status?: 'ACTIVE' | 'INACTIVE' | 'EXPIRED';
    connectedAt?: string;
    [key: string]: unknown;
  };
}

/**
 * User account information
 * Indicates if user is a primary (billing) account or sub-account
 */
export interface UserAccountInfo {
  accountType: 'ROOT' | 'SUB_ACCOUNT' | 'GRADUATED';
  parentUserId?: string; // If sub-account, ID of parent account
  rootUserId: string; // Root account ID
}

/**
 * OAuth initiation request payload
 */
export interface OAuthInitiateRequest {
  organizationId: string;
  userId?: string;
  scopes?: string[];
  redirectUri?: string;
  state?: string;
}

/**
 * OAuth initiation response
 */
export interface OAuthInitiateResponse {
  authorizationUrl: string;
  state: string;
  expiresAt: string;
  sessionId?: string;
}

/**
 * OAuth callback response after successful authorization
 */
export interface OAuthCallbackResponse {
  connectionId: string;
  organizationId: string;
  userId: string;
  status: 'connected' | 'error' | 'pending';
  connectedAt: string;
  scopes: string[];
  rcAccountId?: string;
  rcExtensions?: RingCentralExtension[];
  error?: string;
}

/**
 * RingCentral extension information
 */
export interface RingCentralExtension {
  id: string;
  extensionNumber: string;
  name: string;
  type: 'User' | 'Department' | 'Announcement' | 'Voicemail' | 'SharedLinesGroup';
  status: 'Enabled' | 'Disabled' | 'NotActivated';
  email?: string;
  phoneNumbers?: Array<{
    phoneNumber: string;
    type: 'VoiceFax' | 'FaxOnly' | 'VoiceOnly';
    usageType: 'DirectNumber' | 'MainCompanyNumber' | 'CompanyFaxNumber';
  }>;
}

/**
 * Complete RingCentral connection information
 */
export interface RingCentralConnection {
  connectionId: string;
  organizationId: string;
  userId: string;
  status: 'connected' | 'disconnected' | 'pending' | 'error';
  connectedAt?: string;
  lastSyncAt?: string;
  scopes: string[];
  rcAccountId?: string;
  rcAccountName?: string;
  rcExtensions?: RingCentralExtension[];
  error?: string;
  syncStatus?: {
    callsLastSynced?: string;
    messagesLastSynced?: string;
    contactsLastSynced?: string;
  };
}

/**
 * RingCentral webhook subscription information
 */
export interface WebhookSubscription {
  id: string;
  uri: string;
  eventFilters: string[];
  deliveryMode: {
    transportType: 'WebHook';
    address: string;
    subscriberKey?: string;
    secretKey?: string;
  };
  status: 'Active' | 'Suspended' | 'Blacklisted';
  creationTime: string;
  expirationTime: string;
}

/**
 * RingCentral account information
 */
export interface RingCentralAccount {
  id: string;
  name: string;
  mainNumber: string;
  status: 'Confirmed' | 'Unconfirmed';
  serviceInfo: {
    uri: string;
    brand: {
      id: string;
      name: string;
    };
    servicePlan: {
      id: string;
      name: string;
    };
  };
  operator?: {
    id: string;
    extensionNumber: string;
  };
}

/**
 * Complete state interface for RingCentral store
 */
export interface RingCentralState {
  // Organization related
  organization: Organization | null;
  organizationId: string | null;

  // Connection related
  connections: Map<string, RingCentralConnection>;
  currentConnection: RingCentralConnection | null;
  connectionStatus: 'connected' | 'disconnected' | 'pending' | 'error' | null;

  // OAuth related
  oauthState: string | null;
  oauthSessionId: string | null;

  // Account information
  rcAccount: RingCentralAccount | null;

  // Webhook subscriptions
  webhookSubscriptions: WebhookSubscription[];

  // UI state
  loading: boolean;
  error: string | null;
  lastError: Error | null;
}

/**
 * OAuth scopes available for RingCentral integration
 */
export enum RingCentralScope {
  READ_ACCOUNTS = 'ReadAccounts',
  READ_CALL_LOG = 'ReadCallLog',
  READ_CALL_RECORDING = 'ReadCallRecording',
  READ_MESSAGES = 'ReadMessages',
  READ_CONTACTS = 'ReadContacts',
  WEBHOOK_SUBSCRIPTIONS = 'SubscriptionWebhook',
  READ_PRESENCE = 'ReadPresence',
  EDIT_PRESENCE = 'EditPresence',
  SMS = 'SMS',
  INTERNAL_MESSAGES = 'InternalMessages',
  READ_CLIENT_INFO = 'ReadClientInfo',
  EDIT_EXTENSIONS = 'EditExtensions',
  READ_EXTENSIONS = 'ReadExtensions',
}

/**
 * Default OAuth configuration
 */
export interface OAuthConfig {
  scopes: RingCentralScope[];
  redirectUri?: string;
  expiresIn?: number;
}

/**
 * API Error class for RingCentral specific errors
 */
export class RingCentralAPIError extends Error {
  constructor(
    public status: number,
    message: string,
    public code?: string,
    public details?: any,
  ) {
    super(message);
    this.name = 'RingCentralAPIError';
  }
}

/**
 * Connection management operations
 */
export interface ConnectionOperations {
  connect: (orgId: string, config?: OAuthConfig) => Promise<void>;
  disconnect: (orgId: string) => Promise<void>;
  refresh: (orgId: string) => Promise<RingCentralConnection>;
  sync: (orgId: string) => Promise<void>;
  getStatus: (orgId: string) => Promise<RingCentralConnection | null>;
}

/**
 * Webhook event types
 */
export enum WebhookEventType {
  INSTANT_MESSAGE_EVENT = '/restapi/v1.0/account/~/extension/~/message-store/instant?type=SMS',
  CALL_LOG_EVENT = '/restapi/v1.0/account/~/extension/~/call-log',
  PRESENCE_EVENT = '/restapi/v1.0/account/~/extension/~/presence',
  DETAILED_PRESENCE_EVENT = '/restapi/v1.0/account/~/extension/~/presence?detailedTelephonyState=true',
}

/**
 * Sync configuration for different data types
 */
export interface SyncConfig {
  calls: {
    enabled: boolean;
    interval: number; // minutes
    lookbackDays: number;
  };
  messages: {
    enabled: boolean;
    interval: number;
    lookbackDays: number;
  };
  contacts: {
    enabled: boolean;
    interval: number;
  };
  presence: {
    enabled: boolean;
    realtime: boolean;
  };
}

/**
 * Default sync configuration
 */
export const DEFAULT_SYNC_CONFIG: SyncConfig = {
  calls: {
    enabled: true,
    interval: 15, // sync every 15 minutes
    lookbackDays: 30,
  },
  messages: {
    enabled: true,
    interval: 5, // sync every 5 minutes
    lookbackDays: 7,
  },
  contacts: {
    enabled: true,
    interval: 60, // sync every hour
  },
  presence: {
    enabled: false,
    realtime: false,
  },
};

/**
 * Integration status with detailed information
 */
export interface IntegrationStatus {
  isConnected: boolean;
  hasValidToken: boolean;
  lastSyncTime?: string;
  syncErrors?: string[];
  accountInfo?: {
    name: string;
    mainNumber: string;
    extensionCount: number;
  };
  permissions: {
    canReadCalls: boolean;
    canReadMessages: boolean;
    canReadContacts: boolean;
    canManageWebhooks: boolean;
  };
}
